'use client';

import React, { useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { cn, Header, Nav } from '@/components/ui';
import { TechnoConixLogo } from '@/components/brand';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState } from '@/redux/store';
import { closeSidebar, setDynamicState, toggleTheme } from '@/redux/store/features';
import BurgerMenu from './BurgerMenu';
import { links } from '@/constants/navbarMenu';
import AppLink from '@/components/i18n/AppLink';
import { LocaleDropdown, NavDropdown, Toggle } from '@/components/widget';
import { MoonIcon, SunIcon } from '@/assets/icons';

const Navbar = () => {
  const t = useTranslations('layout');
  const dispatch = useDispatch();
  const sidebarOpen = useSelector((state: RootState) => state.features?.sidebarOpen ?? false);
  const theme = useSelector((state: RootState) => state.features?.theme ?? 'light');
  const scrolled = useSelector((state: RootState) => state.features?.dynamic?.scrolled || false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleScroll = () => {
      dispatch(setDynamicState({ key: 'scrolled', value: window.scrollY > 10 }));
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [dispatch]);

  useEffect(() => {
    if (typeof document === 'undefined' || typeof window === 'undefined') return;

    const mqMobile = window.matchMedia('(max-width: 767px)');

    const syncScrollLock = () => {
      const mobile = mqMobile.matches;
      if (!mobile && sidebarOpen) {
        dispatch(closeSidebar());
        document.body.style.removeProperty('overflow');
        return;
      }
      if (sidebarOpen && mobile) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.removeProperty('overflow');
      }
    };

    syncScrollLock();
    mqMobile.addEventListener('change', syncScrollLock);
    return () => {
      mqMobile.removeEventListener('change', syncScrollLock);
      document.body.style.removeProperty('overflow');
    };
  }, [sidebarOpen, dispatch]);

  return (
    <Header className={cn('home-header', 'navbar-shell', scrolled ? 'scrolled' : 'unscrolled')}>
      {/* One bar row: brand | desktop links (md+) | utilities. Mobile primary links are not here — they render in the fixed sheet below. */}
      <div className="navbar-inner">
        <AppLink href="/" aria-label={t('brandAriaHome')} title={t('brandAriaHome')} className="navbar-brand scale-none">
          <TechnoConixLogo
            variant={theme === 'dark' ? 'dark' : 'light'}
            aria-label={t('logoAlt')}
            title={t('logoTitle')}
            className="h-5 w-auto md:h-6"
          />
        </AppLink>
        <Nav aria-label={t('navMain')} className="nav navbar-desktop-nav">
          {links.map((link) => (
            <NavDropdown key={link.id} link={link} />
          ))}
        </Nav>
        <div className="navbar-actions flex items-center gap-2">
          <LocaleDropdown className="hidden md:inline-flex" />
          <Toggle
            size="sm"
            checked={theme === 'dark'}
            description={t('themeToggle')}
            label={t('themeToggle')}
            wrapperClassName="hidden shrink-0 md:inline-flex"
            leadingSlot={<SunIcon size={12} className="text-current" />}
            trailingSlot={<MoonIcon size={12} className="text-current" />}
            onChange={() => dispatch(toggleTheme())}
          />
          <BurgerMenu />
        </div>
      </div>
      {/* Sibling of `.navbar-inner` so this panel can stay `position: fixed` under the bar (see `navbar.css`). Hidden md+; small screens only. */}
      <Nav
        aria-label={t('navMobile')}
        className="nav navbar-mobile-panel"
        data-open={sidebarOpen ? 'true' : 'false'}
      >
        <div className="flex justify-end px-4 pt-3 md:hidden">
          <LocaleDropdown />
        </div>
        {links.map((link) => (
          <NavDropdown key={link.id} link={link} />
        ))}
      </Nav>
    </Header>
  );
};

export default Navbar;
